const FORMS_API_BASE = (process.env.REACT_APP_FORMS_API_URL || 'http://localhost:23002/api').replace(
  /\/$/,
  ''
);

const resolveOrgId = () => {
  const storedOrg = localStorage.getItem('forms_org_id');
  if (storedOrg) {
    return storedOrg;
  }

  const selectedBuildingRaw = localStorage.getItem('selectedBuilding');
  if (selectedBuildingRaw) {
    try {
      const building = JSON.parse(selectedBuildingRaw);
      return building?.org_id || building?.organization_id || building?.tenant_id || building?.id || null;
    } catch (error) {
      return null;
    }
  }

  return 'org-demo';
};

const buildHeaders = (options = {}) => {
  const normalizedOptions = typeof options === 'boolean' ? { isFormData: options } : options;
  const { isFormData = false, anonymous = false } = normalizedOptions;
  const headers = {};

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  headers['X-Application'] = 'Dailey Forms';
  headers['X-App-Name'] = 'Dailey Forms';
  headers['X-Client-Id'] = 'dailey-forms';

  if (!anonymous) {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const orgId = resolveOrgId();
    if (orgId) {
      headers['X-Org-Id'] = orgId;
    }
  } else {
    headers['X-External-Client'] = 'public-signer';
  }

  return headers;
};

const request = async (
  path,
  {
    method = 'GET',
    body,
    headers,
    isFormData = false,
    anonymous = false,
    suppressAuthRedirect = false
  } = {}
) => {
  const url = /^https?:\/\//i.test(path) ? path : `${FORMS_API_BASE}${path}`;

  const response = await fetch(url, {
    method,
    body,
    headers: {
      ...buildHeaders({ isFormData, anonymous }),
      ...(headers || {})
    }
  });

  if (response.status === 401 && !anonymous && !suppressAuthRedirect) {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login?session=expired';
    throw new Error('Session expired. Please login again.');
  }

  if (response.status === 401 && anonymous) {
    throw new Error('Unauthorized');
  }

  return response;
};

const parseJson = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(text);
  }
};

const normalizeTemplate = (payload) => {
  const schema = payload.schema || {};
  return {
    id: payload.id,
    orgId: payload.orgId,
    name: payload.name,
    version: payload.version,
    tags: payload.tags || [],
    cycle: payload.cycle || null,
    status: schema.status || 'draft',
    template_type: schema.template_type || 'builder',
    description: schema.description || '',
    owner_username: schema.owner_username || null,
    fields: schema.fields || [],
    attachments: schema.attachments || [],
    schema,
    created_at: payload.createdAt || payload.created_at,
    updated_at: payload.updatedAt || payload.updated_at,
    archived_at: payload.archivedAt || payload.archived_at || null
  };
};

export const listTemplates = async () => {
  const response = await request('/templates');
  if (!response.ok) {
    throw new Error(`Failed to load templates (${response.status})`);
  }
  const data = await parseJson(response);
  const collection = Array.isArray(data?.templates) ? data.templates : Array.isArray(data) ? data : [];
  return collection.map(normalizeTemplate);
};

export const createTemplate = async ({ name, schema, tags = [], cycle = null }) => {
  const response = await request('/templates', {
    method: 'POST',
    body: JSON.stringify({
      name,
      schema,
      tags,
      cycle
    })
  });

  if (!response.ok) {
    const message = await response.text();
    console.error('Create template failed', response.status, message);
    throw new Error(message || 'Failed to create template');
  }

  return normalizeTemplate(await parseJson(response));
};

export const getTemplate = async (templateId) => {
  const response = await request(`/templates/${templateId}`);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to load template (${response.status})`);
  }
  return normalizeTemplate(await parseJson(response));
};

export const updateTemplate = async (templateId, payload) => {
  const response = await request(`/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to update template');
  }

  return normalizeTemplate(await parseJson(response));
};

export const archiveTemplate = async (templateId) => {
  const response = await request(`/templates/${templateId}`, {
    method: 'DELETE'
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to archive template');
  }

  return normalizeTemplate(await parseJson(response));
};

const normalizeSubmission = (payload) => ({
  id: payload.id,
  template_id: payload.templateId || payload.template_id,
  org_id: payload.orgId || payload.org_id,
  assigned_to: payload.assignedTo || payload.assigned_to,
  assigned_by: payload.assignedBy || payload.assigned_by,
  due_date: payload.dueDate || payload.due_date,
  status: payload.status,
  submitted_at: payload.submittedAt || payload.submitted_at,
  metadata: payload.metadata || {},
  created_at: payload.createdAt || payload.created_at,
  updated_at: payload.updatedAt || payload.updated_at,
  cancelled_at: payload.cancelledAt || payload.cancelled_at || null
});

const ensurePlainObject = (value, fallback = {}) => {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
      return fallback;
    }
  }
  return fallback;
};

const isBrowser = typeof window !== 'undefined';
const DEV_PACKET_KEY = 'dailey_forms_dev_packets';
const PACKET_STATUS_EVENT_MAP = {
  sent: 'packet_sent',
  completed: 'packet_completed',
  expired: 'packet_expired'
};
const SIGNER_STATUS_EVENT_MAP = {
  invited: 'signer_invited',
  viewed: 'signer_viewed',
  signed: 'signer_signed',
  declined: 'signer_declined',
  completed: 'signer_signed'
};

const loadDevPacketStore = () => {
  if (!isBrowser) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(DEV_PACKET_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Unable to parse dev packet store', error);
    return [];
  }
};

const saveDevPacketStore = (packets) => {
  if (!isBrowser) {
    return;
  }
  try {
    window.localStorage.setItem(DEV_PACKET_KEY, JSON.stringify(packets));
  } catch (error) {
    console.warn('Unable to persist dev packet store', error);
  }
};

const generateDevId = (prefix) => {
  if (isBrowser && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildDevPacket = (payload) => {
  const nowIso = new Date().toISOString();
  const packetId = generateDevId('packet');
  const metadata = ensurePlainObject(payload.metadata, {});
  const status = payload.status || 'draft';

  const signers = Array.isArray(payload.signers)
    ? payload.signers.map((signer, index) => {
        const signerId = generateDevId('signer');
        const signerStatus = signer.status || 'invited';
        const invitedAt = nowIso;
        const completedAt = signerStatus === 'completed' || signerStatus === 'signed' ? nowIso : null;
        return {
          id: signerId,
          packet_id: packetId,
          signer_order: signer.signer_order ?? index + 1,
          signer_type: signer.signer_type || 'external',
          individual_id: signer.individual_id || null,
          employee_id: signer.employee_id || null,
          external_name: signer.external_name || signer.external_email || `Signer ${index + 1}`,
          external_email: signer.external_email || '',
          auth_method: signer.auth_method || 'email_otp',
          status: signerStatus,
          invited_at: invitedAt,
          viewed_at: signerStatus === 'viewed' || signerStatus === 'signed' ? invitedAt : null,
          completed_at: completedAt,
          declined_reason: signer.declined_reason || null,
          reminder_count: signer.reminder_count || 0,
          access_code_hash: signer.access_code_hash || null,
          signature_data: signer.signature_data || null,
          initials_data: signer.initials_data || null,
          event_history: []
        };
      })
    : [];

  const events = [
    {
      id: generateDevId('event'),
      packet_id: packetId,
      signer_id: null,
      event_type: 'packet_created',
      event_details: { createdBy: 'dev-user' },
      event_ip: null,
      user_agent: 'dev',
      created_at: nowIso
    }
  ];

  if (status === 'sent' || status === 'in_progress' || status === 'completed') {
    events.push({
      id: generateDevId('event'),
      packet_id: packetId,
      signer_id: null,
      event_type: 'packet_sent',
      event_details: { sentBy: 'dev-user' },
      event_ip: null,
      user_agent: 'dev',
      created_at: nowIso
    });
  }

  const packetRecord = {
    id: packetId,
    template_id: payload.template_id,
    template_name: null,
    title: payload.title,
    status,
    due_date: payload.due_date || null,
    sent_at: status === 'sent' || status === 'in_progress' || status === 'completed' ? nowIso : null,
    completed_at: status === 'completed' ? nowIso : null,
    created_by: 0,
    created_by_username: 'dev-user',
    updated_by: 0,
    updated_by_username: 'dev-user',
    context_individual_id: payload.context_individual_id || null,
    context_employee_id: payload.context_employee_id || null,
    metadata,
    created_at: nowIso,
    updated_at: nowIso,
    documents: [],
    signers,
    events
  };

  return packetRecord;
};

const updateDevPacketRecord = (packetId, mutator) => {
  const packets = loadDevPacketStore();
  const index = packets.findIndex((entry) => entry.id === packetId);
  if (index === -1) {
    return null;
  }

  const draft = JSON.parse(JSON.stringify(packets[index]));
  const result = mutator(draft);
  if (!result) {
    return null;
  }

  const updated = {
    ...result,
    updated_at: new Date().toISOString()
  };

  packets[index] = updated;
  saveDevPacketStore(packets);
  return updated;
};

const normalizePacketEvent = (payload = {}) => ({
  id: payload.id,
  packet_id: payload.packet_id,
  signer_id: payload.signer_id,
  event_type: payload.event_type,
  event_details: ensurePlainObject(payload.event_details, null),
  event_ip: payload.event_ip || null,
  user_agent: payload.user_agent || null,
  created_at: payload.created_at
});

const normalizePacketDocument = (payload = {}) => ({
  id: payload.id,
  packet_id: payload.packet_id,
  generated_pdf_path: payload.generated_pdf_path,
  checksum: payload.checksum,
  storage_provider: payload.storage_provider,
  created_at: payload.created_at,
  finalized_at: payload.finalized_at
});

const normalizePacketSigner = (payload = {}) => ({
  id: payload.id,
  packet_id: payload.packet_id,
  signer_order: payload.signer_order,
  signer_type: payload.signer_type,
  individual_id: payload.individual_id,
  employee_id: payload.employee_id,
  external_name: payload.external_name,
  external_email: payload.external_email,
  auth_method: payload.auth_method,
  status: payload.status,
  invited_at: payload.invited_at,
  viewed_at: payload.viewed_at,
  completed_at: payload.completed_at,
  declined_reason: payload.declined_reason,
  reminder_count: payload.reminder_count,
  access_code_hash: payload.access_code_hash,
  signature_data: payload.signature_data || null,
  initials_data: payload.initials_data || null,
  event_history: Array.isArray(payload.event_history)
    ? payload.event_history.map(normalizePacketEvent)
    : []
});

const normalizePacket = (payload = {}) => ({
  id: payload.id,
  template_id: payload.template_id,
  template_name: payload.template_name,
  title: payload.title,
  status: payload.status,
  due_date: payload.due_date,
  sent_at: payload.sent_at,
  completed_at: payload.completed_at,
  created_by: payload.created_by,
  created_by_username: payload.created_by_username,
  updated_by: payload.updated_by,
  updated_by_username: payload.updated_by_username,
  context_individual_id: payload.context_individual_id,
  context_employee_id: payload.context_employee_id,
  metadata: ensurePlainObject(payload.metadata, {}),
  created_at: payload.created_at,
  updated_at: payload.updated_at,
  documents: Array.isArray(payload.documents)
    ? payload.documents.map(normalizePacketDocument)
    : [],
  signers: Array.isArray(payload.signers) ? payload.signers.map(normalizePacketSigner) : [],
  events: Array.isArray(payload.events) ? payload.events.map(normalizePacketEvent) : undefined
});

export const listFormSubmissions = async (query = {}) => {
  const params = new URLSearchParams(query);
  const path = params.toString() ? `/forms?${params.toString()}` : '/forms';
  const response = await request(path);
  if (!response.ok) {
    throw new Error(`Failed to load form submissions (${response.status})`);
  }
  const data = await parseJson(response);
  return Array.isArray(data?.submissions) ? data.submissions.map(normalizeSubmission) : [];
};

export const assignForms = async ({ templateId, assignees, dueDate, metadata }) => {
  const response = await request('/forms/assign', {
    method: 'POST',
    body: JSON.stringify({
      template_id: templateId,
      assigned_to: assignees,
      due_date: dueDate,
      metadata
    })
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to assign forms');
  }

  const payload = await parseJson(response);
  return Array.isArray(payload?.submissions) ? payload.submissions.map(normalizeSubmission) : [];
};

export const getFormSubmission = async (formId) => {
  const response = await request(`/forms/${formId}`);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to load submission (${response.status})`);
  }
  return normalizeSubmission(await parseJson(response));
};

export const updateFormSubmission = async (formId, updates) => {
  const response = await request(`/forms/${formId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to update submission');
  }

  return normalizeSubmission(await parseJson(response));
};

export const signFormSubmission = async (formId, payload) => {
  const response = await request(`/forms/${formId}/sign`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to sign form');
  }

  return await parseJson(response);
};

export const fetchSignedDocument = async (formId) => {
  const response = await request(`/forms/${formId}/download`);
  if (response.status === 404 || response.status === 409) {
    return { status: response.status, data: await parseJson(response) };
  }
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to fetch signed document');
  }
  return { status: 200, data: await parseJson(response) };
};

export const fetchPublicForm = async (token) => {
  const response = await request(`/public/forms/${token}`, {
    anonymous: true,
    suppressAuthRedirect: true
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to load form');
  }

  return await parseJson(response);
};

export const signPublicForm = async (token, payload) => {
  const response = await request(`/public/forms/${token}/sign`, {
    method: 'POST',
    anonymous: true,
    suppressAuthRedirect: true,
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to submit signature');
  }

  return await parseJson(response);
};

export const listFormPackets = async (query = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      params.append(key, value);
    });
    const path = params.toString() ? `/form-packets?${params.toString()}` : '/form-packets';
    const response = await request(path);
    if (response.status === 404) {
      return loadDevPacketStore().map(normalizePacket);
    }
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Failed to load form packets (${response.status})`);
    }
    const payload = await parseJson(response);
    return Array.isArray(payload) ? payload.map(normalizePacket) : [];
  } catch (error) {
    console.warn('Falling back to dev packet store (list)', error);
    return loadDevPacketStore().map(normalizePacket);
  }
};

export const getFormPacket = async (packetId) => {
  try {
    const response = await request(`/form-packets/${packetId}`);
    if (response.status === 404) {
      const packets = loadDevPacketStore();
      const packet = packets.find((entry) => entry.id === packetId);
      return packet ? normalizePacket(packet) : null;
    }
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Failed to load form packet (${response.status})`);
    }
    return normalizePacket(await parseJson(response));
  } catch (error) {
    console.warn('Falling back to dev packet store (get)', error);
    const packets = loadDevPacketStore();
    const packet = packets.find((entry) => entry.id === packetId);
    return packet ? normalizePacket(packet) : null;
  }
};

export const createFormPacket = async (payload) => {
  try {
    const response = await request('/form-packets', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (response.status === 404) {
      const packetRecord = buildDevPacket(payload);
      const packets = loadDevPacketStore();
      packets.unshift(packetRecord);
      saveDevPacketStore(packets);
      return normalizePacket(packetRecord);
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to create form packet');
    }

    return normalizePacket(await parseJson(response));
  } catch (error) {
    console.warn('Falling back to dev packet store (create)', error);
    const packetRecord = buildDevPacket(payload);
    const packets = loadDevPacketStore();
    packets.unshift(packetRecord);
    saveDevPacketStore(packets);
    return normalizePacket(packetRecord);
  }
};

export const updateFormPacket = async (packetId, payload) => {
  try {
    const response = await request(`/form-packets/${packetId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    if (response.status === 404) {
      const updatedRecord = updateDevPacketRecord(packetId, (record) => {
        if (payload.title !== undefined) {
          record.title = payload.title;
        }
        if (payload.status !== undefined) {
          record.status = payload.status;
          const mapped = PACKET_STATUS_EVENT_MAP[payload.status];
          if (mapped) {
            record.events = record.events || [];
            record.events.push({
              id: generateDevId('event'),
              packet_id: packetId,
              signer_id: null,
              event_type: mapped,
              event_details: { status: payload.status },
              event_ip: null,
              user_agent: 'dev',
              created_at: new Date().toISOString()
            });
          }
        }
        if (payload.due_date !== undefined) {
          record.due_date = payload.due_date;
        }
        if (payload.sent_at !== undefined) {
          record.sent_at = payload.sent_at;
        }
        if (payload.completed_at !== undefined) {
          record.completed_at = payload.completed_at;
        }
        if (payload.metadata !== undefined) {
          record.metadata = ensurePlainObject(payload.metadata, {});
        }
        if (Array.isArray(payload.signers)) {
          record.signers = payload.signers.map((signer, index) => {
            const existing =
              record.signers?.find(
                (item) =>
                  item.id === signer.id ||
                  String(item.id) === String(signer.id) ||
                  item.external_email === signer.external_email
              ) || {};
            return {
              ...existing,
              id: existing.id || generateDevId('signer'),
              packet_id: packetId,
              signer_order: signer.signer_order ?? index + 1,
              signer_type: signer.signer_type || 'external',
              individual_id: signer.individual_id || null,
              employee_id: signer.employee_id || null,
              external_name: signer.external_name || existing.external_name || signer.external_email,
              external_email: signer.external_email || existing.external_email || '',
              auth_method: signer.auth_method || existing.auth_method || 'email_otp',
              status: signer.status || existing.status || 'invited',
              invited_at: signer.invited_at || existing.invited_at || new Date().toISOString(),
              viewed_at: signer.viewed_at || existing.viewed_at || null,
              completed_at: signer.completed_at || existing.completed_at || null,
              declined_reason: signer.declined_reason || existing.declined_reason || null,
              reminder_count: signer.reminder_count ?? existing.reminder_count ?? 0,
              access_code_hash: signer.access_code_hash || existing.access_code_hash || null,
              signature_data: signer.signature_data || existing.signature_data || null,
              initials_data: signer.initials_data || existing.initials_data || null,
              event_history: Array.isArray(existing.event_history)
                ? existing.event_history
                : []
            };
          });
        }
        return record;
      });

      if (!updatedRecord) {
        const newlyCreated = buildDevPacket(payload);
        const packets = loadDevPacketStore();
        packets.unshift(newlyCreated);
        saveDevPacketStore(packets);
        return normalizePacket(newlyCreated);
      }

      return normalizePacket(updatedRecord);
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to update form packet');
    }

    return normalizePacket(await parseJson(response));
  } catch (error) {
    console.warn('Falling back to dev packet store (update)', error);
    const updatedRecord = updateDevPacketRecord(packetId, (record) => {
      if (payload.title !== undefined) {
        record.title = payload.title;
      }
      if (payload.status !== undefined) {
        record.status = payload.status;
      }
      if (payload.due_date !== undefined) {
        record.due_date = payload.due_date;
      }
      if (payload.sent_at !== undefined) {
        record.sent_at = payload.sent_at;
      }
      if (payload.completed_at !== undefined) {
        record.completed_at = payload.completed_at;
      }
      if (payload.metadata !== undefined) {
        record.metadata = ensurePlainObject(payload.metadata, {});
      }
      return record;
    });

    if (!updatedRecord) {
      const newlyCreated = buildDevPacket(payload);
      const packets = loadDevPacketStore();
      packets.unshift(newlyCreated);
      saveDevPacketStore(packets);
      return normalizePacket(newlyCreated);
    }

    return normalizePacket(updatedRecord);
  }
};

export const updatePacketSignerStatus = async (packetId, signerId, payload) => {
  try {
    const response = await request(`/form-packets/${packetId}/signers/${signerId}/status`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (response.status === 404) {
      const updatedRecord = updateDevPacketRecord(packetId, (record) => {
        const signerIndex = record.signers?.findIndex(
          (signer) => String(signer.id) === String(signerId)
        );
        if (signerIndex === undefined || signerIndex === -1) {
          return record;
        }
        const signer = { ...record.signers[signerIndex] };

        if (payload.status !== undefined) {
          signer.status = payload.status;
        }
        if (payload.invited_at !== undefined) {
          signer.invited_at = payload.invited_at;
        }
        if (payload.viewed_at !== undefined) {
          signer.viewed_at = payload.viewed_at;
        }
        if (payload.completed_at !== undefined) {
          signer.completed_at = payload.completed_at;
        }
        if (payload.declined_reason !== undefined) {
          signer.declined_reason = payload.declined_reason;
        }
        if (payload.reminder_count !== undefined) {
          signer.reminder_count = payload.reminder_count;
        }
        if (payload.signature_data !== undefined) {
          signer.signature_data = payload.signature_data || null;
        }
        if (payload.initials_data !== undefined) {
          signer.initials_data = payload.initials_data || null;
        }

        const effectiveEvent =
          payload.event_type ||
          (payload.status && SIGNER_STATUS_EVENT_MAP[payload.status]) ||
          null;

        if (effectiveEvent) {
          const event = {
            id: generateDevId('event'),
            packet_id: packetId,
            signer_id: signer.id,
            event_type: effectiveEvent,
            event_details: ensurePlainObject(payload.event_details, {}),
            event_ip: payload.event_ip || null,
            user_agent: payload.user_agent || 'dev',
            created_at: new Date().toISOString()
          };
          record.events = record.events || [];
          record.events.push(event);
          signer.event_history = Array.isArray(signer.event_history)
            ? [...signer.event_history, event]
            : [event];
        }

        record.signers[signerIndex] = signer;
        return record;
      });
      const packets = loadDevPacketStore();
      const packet = updatedRecord || packets.find((entry) => entry.id === packetId);
      const signer =
        packet?.signers?.find((entry) => String(entry.id) === String(signerId)) || null;
      return {
        signer: signer ? normalizePacketSigner(signer) : null
      };
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to update signer status');
    }

    const data = await parseJson(response);
    return {
      signer: normalizePacketSigner(data?.signer || {})
    };
  } catch (error) {
    console.warn('Falling back to dev packet store (update signer)', error);
    const updatedRecord = updateDevPacketRecord(packetId, (record) => {
      const signerIndex = record.signers?.findIndex(
        (signer) => String(signer.id) === String(signerId)
      );
      if (signerIndex === undefined || signerIndex === -1) {
        return record;
      }
      const signer = { ...record.signers[signerIndex] };
      if (payload.status !== undefined) {
        signer.status = payload.status;
      }
      if (payload.invited_at !== undefined) {
        signer.invited_at = payload.invited_at;
      }
      if (payload.viewed_at !== undefined) {
        signer.viewed_at = payload.viewed_at;
      }
      if (payload.completed_at !== undefined) {
        signer.completed_at = payload.completed_at;
      }
      if (payload.declined_reason !== undefined) {
        signer.declined_reason = payload.declined_reason;
      }
      signer.event_history = Array.isArray(signer.event_history) ? signer.event_history : [];
      const effectiveEvent =
        payload.event_type ||
        (payload.status && SIGNER_STATUS_EVENT_MAP[payload.status]) ||
        null;
      if (effectiveEvent) {
        const event = {
          id: generateDevId('event'),
          packet_id: packetId,
          signer_id: signer.id,
          event_type: effectiveEvent,
          event_details: ensurePlainObject(payload.event_details, {}),
          event_ip: payload.event_ip || null,
          user_agent: payload.user_agent || 'dev',
          created_at: new Date().toISOString()
        };
        record.events = record.events || [];
        record.events.push(event);
        signer.event_history.push(event);
      }
      record.signers[signerIndex] = signer;
      return record;
    });
    const packets = loadDevPacketStore();
    const packet = updatedRecord || packets.find((entry) => entry.id === packetId);
    const signer =
      packet?.signers?.find((entry) => String(entry.id) === String(signerId)) || null;
    return {
      signer: signer ? normalizePacketSigner(signer) : null
    };
  }
};

export const recordPacketEvent = async (packetId, payload) => {
  try {
    const response = await request(`/form-packets/${packetId}/events`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (response.status === 404) {
      const updatedRecord = updateDevPacketRecord(packetId, (record) => {
        const event = {
          id: generateDevId('event'),
          packet_id: packetId,
          signer_id: payload.signer_id || null,
          event_type: payload.event_type,
          event_details: ensurePlainObject(payload.event_details, {}),
          event_ip: payload.event_ip || null,
          user_agent: payload.user_agent || 'dev',
          created_at: new Date().toISOString()
        };
        record.events = record.events || [];
        record.events.push(event);
        if (event.signer_id) {
          const signerIndex = record.signers?.findIndex(
            (signer) => String(signer.id) === String(event.signer_id)
          );
          if (signerIndex !== undefined && signerIndex >= 0) {
            const signer = { ...record.signers[signerIndex] };
            signer.event_history = Array.isArray(signer.event_history)
              ? [...signer.event_history, event]
              : [event];
            record.signers[signerIndex] = signer;
          }
        }
        return record;
      });
      return updatedRecord ? { message: 'Event recorded (dev)' } : { message: 'Event ignored' };
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to record packet event');
    }

    return await parseJson(response);
  } catch (error) {
    console.warn('Falling back to dev packet store (record event)', error);
    updateDevPacketRecord(packetId, (record) => {
      const event = {
        id: generateDevId('event'),
        packet_id: packetId,
        signer_id: payload.signer_id || null,
        event_type: payload.event_type,
        event_details: ensurePlainObject(payload.event_details, {}),
        event_ip: payload.event_ip || null,
        user_agent: payload.user_agent || 'dev',
        created_at: new Date().toISOString()
      };
      record.events = record.events || [];
      record.events.push(event);
      if (event.signer_id) {
        const signerIndex = record.signers?.findIndex(
          (signer) => String(signer.id) === String(event.signer_id)
        );
        if (signerIndex !== undefined && signerIndex >= 0) {
          const signer = { ...record.signers[signerIndex] };
          signer.event_history = Array.isArray(signer.event_history)
            ? [...signer.event_history, event]
            : [event];
          record.signers[signerIndex] = signer;
        }
      }
      return record;
    });
    return { message: 'Event recorded (dev)' };
  }
};
